import {
  fleetEnergyCommandLabel,
  fleetEnergyStations,
  fleetFirmwareStatusLabel,
  type FleetEnergyOverview,
} from '@gb-ticket/shared-types';
import { type FormEvent, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { FleetChargeConnectorCard } from '@/products/fleet/features/fleet-charge-status';
import { fleetPanelClassName } from '@/products/fleet/features/fleet-panel';
import {
  useCreateFleetTariffChip,
  useDeleteFleetEnergyGroup,
  useFleetEnergy,
  useFleetEnergyCommand,
  useFleetSepaFile,
  usePayFleetInvoice,
  useReleaseFleetTariffChip,
  useSaveFleetEnergyGroup,
  useSaveFleetOcmfKey,
  useSaveFleetSessionOcmf,
  useSaveFleetTariff,
  useSetFleetConnectorLimit,
  useSetFleetEnergyDeparture,
  useUpdateFleetTariffChip,
} from '@/products/fleet/features/use-fleet';

function formText(value: FormDataEntryValue | null): string {
  return typeof value === 'string' ? value : '';
}

function fail(error: unknown, fallback: string) {
  toast.error(error instanceof ApiRequestError ? error.message : fallback);
}

function euro(cents: number): string {
  return `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function tariffClassLabel(value: 'fleet' | 'guest' | 'employee'): string {
  if (value === 'fleet') return 'Fuhrpark';
  if (value === 'guest') return 'Gast';
  return 'Mitarbeiter';
}

function centsField(value: FormDataEntryValue | null): number | null {
  const text = formText(value).trim();
  if (!text) return null;
  const parsed = Number(text);
  return Number.isInteger(parsed) ? parsed : Number.NaN;
}

function TariffPanel({ overview }: { overview: FleetEnergyOverview }) {
  const save = useSaveFleetTariff();
  const createChip = useCreateFleetTariffChip();
  const release = useReleaseFleetTariffChip();
  const updateChip = useUpdateFleetTariffChip();
  const pay = usePayFleetInvoice();
  const sepa = useFleetSepaFile();
  const tariff = overview.tariff;

  async function onTariff(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const guestNetCentsPerKwh = centsField(form.get('guestNetCentsPerKwh'));
    const employeeNetCentsPerKwh = centsField(form.get('employeeNetCentsPerKwh'));
    const quietMinutes = Number(formText(form.get('quietMinutes')));
    try {
      await save.mutateAsync({
        guestNetCentsPerKwh,
        employeeNetCentsPerKwh,
        guestRequiresRelease: form.get('guestRequiresRelease') === 'on',
        quietMinutes,
        creditorName: formText(form.get('creditorName')).trim() || null,
        creditorId: formText(form.get('creditorId')).trim() || null,
        creditorIban: formText(form.get('creditorIban')).trim() || null,
      });
      toast.success('Tarif gespeichert.');
    } catch (error) {
      fail(error, 'Tarif konnte nicht gespeichert werden.');
    }
  }

  async function onChip(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const chipClass = formText(form.get('class'));
    if (chipClass !== 'guest' && chipClass !== 'employee') return;
    try {
      await createChip.mutateAsync({
        idTag: formText(form.get('idTag')).trim(),
        class: chipClass,
        name: formText(form.get('name')).trim(),
        email: formText(form.get('email')).trim(),
        street: formText(form.get('street')).trim(),
        postalCode: formText(form.get('postalCode')).trim(),
        city: formText(form.get('city')).trim(),
        iban: formText(form.get('iban')).trim(),
        mandateDate: formText(form.get('mandateDate')) || null,
      });
      formElement.reset();
      toast.success('Chip eingetragen.');
    } catch (error) {
      fail(error, 'Chip konnte nicht eingetragen werden.');
    }
  }

  async function downloadSepa() {
    try {
      const file = await sepa.mutateAsync();
      const blob = new Blob([file.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      fail(error, 'SEPA-Datei nicht verfügbar.');
    }
  }

  return (
    <section className={`${fleetPanelClassName} space-y-4 p-4`}>
      <h2 className="text-base font-semibold">Tarif</h2>
      <form className="grid gap-3 sm:grid-cols-2" onSubmit={(event) => void onTariff(event)}>
        <Label className="space-y-1">
          <span>Gast, Cent netto je kWh</span>
          <Input
            name="guestNetCentsPerKwh"
            inputMode="numeric"
            defaultValue={tariff.guestNetCentsPerKwh ?? ''}
            aria-label="Gast, Cent netto je kWh"
          />
        </Label>
        <Label className="space-y-1">
          <span>Mitarbeiter, Cent netto je kWh</span>
          <Input
            name="employeeNetCentsPerKwh"
            inputMode="numeric"
            defaultValue={tariff.employeeNetCentsPerKwh ?? ''}
            aria-label="Mitarbeiter, Cent netto je kWh"
          />
        </Label>
        <Label className="flex items-center gap-2">
          <input
            type="checkbox"
            name="guestRequiresRelease"
            defaultChecked={tariff.guestRequiresRelease}
          />
          Gast erst nach Freigabe
        </Label>
        <Label className="space-y-1">
          <span>Ruhezeit in Minuten</span>
          <Input
            name="quietMinutes"
            inputMode="numeric"
            defaultValue={String(tariff.quietMinutes)}
            aria-label="Ruhezeit in Minuten"
            required
          />
        </Label>
        <Label className="space-y-1">
          <span>Gläubiger-Name</span>
          <Input
            name="creditorName"
            defaultValue={tariff.creditorName ?? ''}
            aria-label="Gläubiger-Name"
          />
        </Label>
        <Label className="space-y-1">
          <span>Gläubiger-ID</span>
          <Input
            name="creditorId"
            defaultValue={tariff.creditorId ?? ''}
            aria-label="Gläubiger-ID"
          />
        </Label>
        <Label className="space-y-1 sm:col-span-2">
          <span>Gläubiger-IBAN</span>
          <Input
            name="creditorIban"
            defaultValue={tariff.creditorIban ?? ''}
            aria-label="Gläubiger-IBAN"
          />
        </Label>
        <Button type="submit" disabled={save.isPending}>
          Tarif speichern
        </Button>
      </form>
      <form className="grid gap-3 sm:grid-cols-2" onSubmit={(event) => void onChip(event)}>
        <Input name="idTag" aria-label="Chip" required />
        <select
          name="class"
          aria-label="Klasse"
          defaultValue="guest"
          className="h-9 rounded-md border px-2"
        >
          <option value="guest">Gast</option>
          <option value="employee">Mitarbeiter</option>
        </select>
        <Input name="name" aria-label="Name" />
        <Input name="email" aria-label="E-Mail" />
        <Input name="street" aria-label="Straße" />
        <Input name="postalCode" aria-label="Postleitzahl" />
        <Input name="city" aria-label="Ort" />
        <Input name="iban" aria-label="IBAN" />
        <Input name="mandateDate" type="date" aria-label="Mandatsdatum" />
        <Button type="submit" variant="outline" disabled={createChip.isPending}>
          Chip eintragen
        </Button>
      </form>
      <ul className="space-y-2 text-sm">
        {overview.chips.map((chip) => (
          <li key={chip.id} className="space-y-2">
            <span>
              {chip.idTag} · {chip.class === 'guest' ? 'Gast' : 'Mitarbeiter'}
              {chip.class === 'guest' && chip.released ? ' · freigegeben' : ''}
            </span>
            {chip.class === 'guest' && !chip.released ? (
              <Button
                type="button"
                variant="outline"
                disabled={release.isPending}
                onClick={() => {
                  void release
                    .mutateAsync(chip.id)
                    .catch((error: unknown) => fail(error, 'Freigabe fehlgeschlagen.'));
                }}
              >
                Freigeben
              </Button>
            ) : null}
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                void updateChip
                  .mutateAsync({
                    id: chip.id,
                    body: {
                      name: formText(form.get('name')).trim(),
                      email: formText(form.get('email')).trim(),
                      street: formText(form.get('street')).trim(),
                      postalCode: formText(form.get('postalCode')).trim(),
                      city: formText(form.get('city')).trim(),
                      iban: formText(form.get('iban')).trim(),
                      mandateDate: formText(form.get('mandateDate')) || null,
                    },
                  })
                  .then(() => toast.success('Chip gespeichert.'))
                  .catch((error: unknown) => fail(error, 'Chip konnte nicht gespeichert werden.'));
              }}
            >
              <Input name="name" aria-label={`Name ${chip.idTag}`} defaultValue={chip.name ?? ''} />
              <Input
                name="email"
                aria-label={`E-Mail ${chip.idTag}`}
                defaultValue={chip.email ?? ''}
              />
              <Input
                name="street"
                aria-label={`Straße ${chip.idTag}`}
                defaultValue={chip.street ?? ''}
              />
              <Input
                name="postalCode"
                aria-label={`Postleitzahl ${chip.idTag}`}
                defaultValue={chip.postalCode ?? ''}
              />
              <Input name="city" aria-label={`Ort ${chip.idTag}`} defaultValue={chip.city ?? ''} />
              <Input name="iban" aria-label={`IBAN ${chip.idTag}`} defaultValue={chip.iban ?? ''} />
              <Input
                name="mandateDate"
                type="date"
                aria-label={`Mandatsdatum ${chip.idTag}`}
                defaultValue={chip.mandateDate ?? ''}
              />
              <Button type="submit" variant="outline" disabled={updateChip.isPending}>
                Daten speichern
              </Button>
            </form>
          </li>
        ))}
      </ul>
      <ul className="space-y-2 text-sm">
        {overview.invoices.map((invoice) => (
          <li key={invoice.id} className="flex flex-wrap items-center gap-2">
            <span>
              Rechnung {invoice.number} · {euro(invoice.grossCents)}
              {invoice.paid ? ' · bezahlt' : ' · offen'}
              {invoice.sent ? ' · gesendet' : ''}
            </span>
            {invoice.paid ? null : (
              <Button
                type="button"
                variant="outline"
                disabled={pay.isPending}
                onClick={() => {
                  void pay
                    .mutateAsync(invoice.id)
                    .catch((error: unknown) => fail(error, 'Konnte nicht abgehakt werden.'));
                }}
              >
                Als bezahlt abhaken
              </Button>
            )}
          </li>
        ))}
      </ul>
      <Button
        type="button"
        variant="outline"
        disabled={sepa.isPending}
        onClick={() => void downloadSepa()}
      >
        SEPA-Datei
      </Button>
    </section>
  );
}

function GroupForm({ choices }: { choices: FleetEnergyOverview['choices'] }) {
  const save = useSaveFleetEnergyGroup();
  const [name, setName] = useState('');
  const [limitAmps, setLimitAmps] = useState('64');
  const [connectorIds, setConnectorIds] = useState<string[]>([]);

  function toggle(id: string) {
    setConnectorIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await save.mutateAsync({
        body: { name, limitAmps: Number(limitAmps), connectorIds },
      });
      setName('');
      setConnectorIds([]);
      toast.success('Ladegruppe angelegt.');
    } catch (error) {
      fail(error, 'Die Gruppe konnte nicht angelegt werden.');
    }
  }

  const free = choices.filter((choice) => choice.groupId == null);
  return (
    <form onSubmit={onSubmit} className={`${fleetPanelClassName} space-y-3 p-4`}>
      <h2 className="text-base font-semibold">Ladegruppe</h2>
      <div className="space-y-1">
        <Label htmlFor="energy-name">Name</Label>
        <Input
          id="energy-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="energy-limit">Obergrenze in Ampere</Label>
        <Input
          id="energy-limit"
          value={limitAmps}
          onChange={(event) => setLimitAmps(event.target.value)}
          required
        />
      </div>
      <fieldset className="space-y-1">
        <legend className="text-sm font-medium">Anschlüsse</legend>
        {free.map((choice) => (
          <label key={choice.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={connectorIds.includes(choice.id)}
              onChange={() => toggle(choice.id)}
            />
            {choice.stationName} Anschluss {choice.connectorNumber}
          </label>
        ))}
      </fieldset>
      <Button type="submit" disabled={save.isPending}>
        Gruppe speichern
      </Button>
    </form>
  );
}

function berlinParts(value: Date) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(value);
}

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((item) => item.type === type)?.value ?? '';
}

function isoToBerlinInput(iso: string): string {
  const parts = berlinParts(new Date(iso));
  return `${part(parts, 'year')}-${part(parts, 'month')}-${part(parts, 'day')}T${part(parts, 'hour')}:${part(parts, 'minute')}`;
}

/** Reads a datetime-local value as Europe/Berlin wall time. */
function berlinInputToIso(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    return new Date(value).toISOString();
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const wall = `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}`;
  for (const offset of [1, 2]) {
    const candidate = new Date(Date.UTC(year, month - 1, day, hour - offset, minute));
    const parts = berlinParts(candidate);
    const shown = `${part(parts, 'year')}-${part(parts, 'month')}-${part(parts, 'day')} ${part(parts, 'hour')}:${part(parts, 'minute')}`;
    if (shown === wall) {
      return candidate.toISOString();
    }
  }
  return new Date(Date.UTC(year, month - 1, day, hour - 1, minute)).toISOString();
}

function DepartureForm({
  sessionId,
  cardId,
  departureAt,
}: {
  sessionId: string;
  cardId: string;
  departureAt: string | null;
}) {
  const depart = useSetFleetEnergyDeparture();
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        const value = formText(new FormData(event.currentTarget).get('departure'));
        const nextDeparture = value ? berlinInputToIso(value) : null;
        void depart
          .mutateAsync({ sessionId, departureAt: nextDeparture })
          .then(() => toast.success('Abfahrt gespeichert.'))
          .catch((error: unknown) => fail(error, 'Abfahrt nicht gespeichert.'));
      }}
    >
      <div className="space-y-1">
        <Label htmlFor={`departure-${cardId}`}>Abfahrt</Label>
        <Input
          id={`departure-${cardId}`}
          name="departure"
          type="datetime-local"
          defaultValue={departureAt ? isoToBerlinInput(departureAt) : ''}
        />
        <p className="text-xs text-muted-foreground">Leer speichern löscht die Abfahrt.</p>
      </div>
      <Button type="submit" variant="outline">
        Abfahrt speichern
      </Button>
    </form>
  );
}

function ConnectorLimitForm({ cardId, limitAmps }: { cardId: string; limitAmps: number | null }) {
  const save = useSetFleetConnectorLimit();
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        const raw = formText(new FormData(event.currentTarget).get('limitAmps')).trim();
        const nextLimit = raw === '' ? null : Number(raw);
        if (nextLimit != null && !Number.isInteger(nextLimit)) {
          toast.error('Die Amperezahl liegt zwischen 6 und 2000 A.');
          return;
        }
        void save
          .mutateAsync({ connectorId: cardId, limitAmps: nextLimit })
          .then(() => toast.success('Ampere gespeichert.'))
          .catch((error: unknown) => fail(error, 'Ampere nicht gespeichert.'));
      }}
    >
      <div className="space-y-1">
        <Label htmlFor={`limit-${cardId}`}>Ampere</Label>
        <Input
          id={`limit-${cardId}`}
          name="limitAmps"
          inputMode="numeric"
          defaultValue={limitAmps == null ? '' : String(limitAmps)}
        />
        <p className="text-xs text-muted-foreground">Leer speichern löscht die Grenze.</p>
      </div>
      <Button type="submit" variant="outline">
        Ampere speichern
      </Button>
    </form>
  );
}

type EnergyCard = FleetEnergyOverview['cards'][number];

function ConnectorCommands({
  card,
  vehicles,
  pending,
  onStart,
  onStop,
  onUnlock,
}: {
  card: EnergyCard;
  vehicles: FleetEnergyOverview['vehicles'];
  pending: boolean;
  onStart: (vehicleId: string, idTag: string) => void;
  onStop: () => void;
  onUnlock: () => void;
}) {
  const [vehicleId, setVehicleId] = useState(vehicles[0]?.id ?? '');
  const vehicle = vehicles.find((item) => item.id === vehicleId) ?? null;
  const [chip, setChip] = useState(vehicle?.chips[0] ?? '');
  const chosenChip = vehicle?.chips.length === 1 ? (vehicle.chips[0] ?? '') : chip;

  return (
    <div className="flex flex-wrap items-end gap-2">
      {card.sessionId ? (
        <Button type="button" variant="outline" disabled={pending} onClick={onStop}>
          Fernstopp
        </Button>
      ) : (
        <>
          <label className="space-y-1 text-sm">
            Fahrzeug
            <select
              aria-label="Fahrzeug"
              className="block rounded-md border bg-background px-2 py-1"
              value={vehicleId}
              onChange={(event) => {
                const next = event.target.value;
                setVehicleId(next);
                const match = vehicles.find((item) => item.id === next);
                setChip(match?.chips[0] ?? '');
              }}
            >
              {vehicles.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.licensePlate}
                </option>
              ))}
            </select>
          </label>
          {vehicle && vehicle.chips.length > 1 ? (
            <label className="space-y-1 text-sm">
              Chip
              <select
                aria-label="Chip"
                className="block rounded-md border bg-background px-2 py-1"
                value={chip}
                onChange={(event) => setChip(event.target.value)}
              >
                {vehicle.chips.map((idTag) => (
                  <option key={idTag} value={idTag}>
                    {idTag}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <Button
            type="button"
            variant="outline"
            disabled={pending || !vehicleId || !chosenChip}
            onClick={() => {
              if (!vehicleId || !chosenChip) {
                return;
              }
              onStart(vehicleId, chosenChip);
            }}
          >
            Fernstart
          </Button>
        </>
      )}
      <Button type="button" variant="outline" disabled={pending} onClick={onUnlock}>
        Entriegeln
      </Button>
    </div>
  );
}

function OcmfKeyForm({ stationId, publicKey }: { stationId: string; publicKey: string | null }) {
  const save = useSaveFleetOcmfKey();
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        const publicKeyText = formText(new FormData(event.currentTarget).get('publicKey'));
        void save
          .mutateAsync({ stationId, publicKey: publicKeyText })
          .then(() => toast.success('Schlüssel gespeichert.'))
          .catch((error: unknown) => fail(error, 'Schlüssel nicht gespeichert.'));
      }}
    >
      <div className="min-w-64 flex-1 space-y-1">
        <Label htmlFor={`ocmf-key-${stationId}`}>Öffentlicher Schlüssel</Label>
        <Input
          id={`ocmf-key-${stationId}`}
          name="publicKey"
          defaultValue={publicKey ?? ''}
          spellCheck={false}
        />
      </div>
      <Button type="submit" variant="outline" disabled={save.isPending}>
        Schlüssel speichern
      </Button>
    </form>
  );
}

function OcmfPasteForm({ sessionId, cardId }: { sessionId: string; cardId: string }) {
  const save = useSaveFleetSessionOcmf();
  return (
    <form
      className="space-y-2"
      onSubmit={(event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const text = formText(new FormData(form).get('ocmf'));
        void save
          .mutateAsync({ sessionId, text })
          .then(() => {
            form.reset();
            toast.success('Datensatz gespeichert.');
          })
          .catch((error: unknown) => fail(error, 'Datensatz nicht gespeichert.'));
      }}
    >
      <div className="space-y-1">
        <Label htmlFor={`ocmf-${cardId}`}>OCMF-Text</Label>
        <Textarea id={`ocmf-${cardId}`} name="ocmf" spellCheck={false} />
      </div>
      <Button type="submit" variant="outline" disabled={save.isPending}>
        Datensatz speichern
      </Button>
    </form>
  );
}

function EnergyBody({
  overview,
  save,
}: {
  overview: FleetEnergyOverview;
  save: ReturnType<typeof useSaveFleetEnergyGroup>;
}) {
  const command = useFleetEnergyCommand();
  const removeGroup = useDeleteFleetEnergyGroup();
  const pendingKey = useRef<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [notice, setNotice] = useState<Record<string, string>>({});

  async function run(key: string, path: string, body?: unknown) {
    if (pendingKey.current === key) {
      return;
    }
    pendingKey.current = key;
    setPending(key);
    try {
      const response = await command.mutateAsync({ path, body });
      setNotice((current) => ({ ...current, [key]: fleetEnergyCommandLabel(response.result) }));
    } catch (error) {
      fail(error, 'Der Befehl konnte nicht gesendet werden.');
    } finally {
      pendingKey.current = null;
      setPending(null);
    }
  }

  function ask(key: string, question: string, path: string, body?: unknown) {
    if (pendingKey.current === key) {
      return;
    }
    if (!window.confirm(question)) {
      return;
    }
    void run(key, path, body);
  }

  function hardReset(stationId: string) {
    const key = `station:${stationId}`;
    if (pendingKey.current === key) {
      return;
    }
    if (!window.confirm('Die Wallbox startet hart neu. Beide Anschlüsse sind betroffen.')) {
      return;
    }
    if (!window.confirm('Wirklich hart neu starten?')) {
      return;
    }
    void run(key, `/fleet/energy/stations/${stationId}/reset`, { type: 'hard' });
  }

  return (
    <>
      <TariffPanel overview={overview} />
      <GroupForm choices={overview.choices} />
      <section className="space-y-3">
        {overview.groups.map((group) => (
          <form
            key={`${group.id}:${group.name}:${group.limitAmps}:${group.connectorIds.join()}`}
            className={`${fleetPanelClassName} space-y-2 p-4`}
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const name = formText(form.get('name'));
              const limitAmps = Number(formText(form.get('limitAmps')));
              const connectorIds = form
                .getAll('connectorId')
                .filter((value): value is string => typeof value === 'string');
              void save
                .mutateAsync({ id: group.id, body: { name, limitAmps, connectorIds } })
                .then(() => toast.success('Ladegruppe gespeichert.'))
                .catch((error: unknown) => fail(error, 'Speichern fehlgeschlagen.'));
            }}
          >
            <Input name="name" defaultValue={group.name} aria-label="Name" required />
            <Input
              name="limitAmps"
              defaultValue={String(group.limitAmps)}
              aria-label="Obergrenze in Ampere"
              required
            />
            {overview.choices
              .filter((choice) => choice.groupId == null || choice.groupId === group.id)
              .map((choice) => (
                <label key={choice.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    name="connectorId"
                    value={choice.id}
                    defaultChecked={group.connectorIds.includes(choice.id)}
                  />
                  {choice.stationName} Anschluss {choice.connectorNumber}
                </label>
              ))}
            <Button type="submit" variant="outline">
              Gruppe speichern
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (
                  !window.confirm(
                    'Die Gruppe wird gelöscht. Die Anschlüsse bleiben. Offene Zuteilungen an diesen Anschlüssen enden.',
                  )
                ) {
                  return;
                }
                void removeGroup
                  .mutateAsync(group.id)
                  .then(() => toast.success('Ladegruppe gelöscht.'))
                  .catch((error: unknown) => fail(error, 'Löschen fehlgeschlagen.'));
              }}
            >
              Gruppe löschen
            </Button>
          </form>
        ))}
      </section>
      <div className="space-y-6">
        {(overview.stations ?? fleetEnergyStations(overview.cards)).map((station) => (
          <section key={station.stationId} className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold">{station.stationName}</h2>
              {station.canCommand ? (
                <div className="flex flex-wrap items-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pending === `station:${station.stationId}`}
                    onClick={() =>
                      void run(
                        `station:${station.stationId}`,
                        `/fleet/energy/stations/${station.stationId}/reset`,
                        {
                          type: 'soft',
                        },
                      )
                    }
                  >
                    Sanft neu starten
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pending === `station:${station.stationId}`}
                    onClick={() => hardReset(station.stationId)}
                  >
                    Hart neu starten
                  </Button>
                  <form
                    className="flex flex-wrap items-end gap-2"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const location = formText(
                        new FormData(event.currentTarget).get('location'),
                      ).trim();
                      ask(
                        `station:${station.stationId}`,
                        'Die Wallbox installiert neue Firmware. Beide Anschlüsse können ausfallen. Wirklich starten?',
                        `/fleet/energy/stations/${station.stationId}/firmware`,
                        { location },
                      );
                    }}
                  >
                    <div className="space-y-1">
                      <Label htmlFor={`firmware-${station.stationId}`}>Firmware-Adresse</Label>
                      <Input id={`firmware-${station.stationId}`} name="location" />
                    </div>
                    <Button
                      type="submit"
                      variant="outline"
                      disabled={pending === `station:${station.stationId}`}
                    >
                      Firmware installieren
                    </Button>
                  </form>
                </div>
              ) : null}
            </div>
            {station.canCommand && station.firmwareVersion ? (
              <p className="text-sm">Firmware-Version {station.firmwareVersion}</p>
            ) : null}
            {station.canCommand && station.firmwareStatus ? (
              <p className="text-sm">
                Firmware-Status {fleetFirmwareStatusLabel(station.firmwareStatus)}
              </p>
            ) : null}
            <OcmfKeyForm
              stationId={station.stationId}
              publicKey={station.cards[0]?.ocmfPublicKey ?? null}
            />
            {notice[`station:${station.stationId}`] ? (
              <p className="text-sm">{notice[`station:${station.stationId}`]}</p>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              {station.cards.map((card) => (
                <div key={card.id} className="space-y-2">
                  <FleetChargeConnectorCard connector={card} />
                  {card.tariffClass ? (
                    <p className="text-sm">
                      {tariffClassLabel(card.tariffClass)}
                      {card.netCentsPerKwh != null
                        ? ` · ${euro(card.netCentsPerKwh)} / kWh netto`
                        : ''}
                      {card.netCents != null && card.vatCents != null && card.grossCents != null
                        ? ` · ${euro(card.netCents)} netto, ${euro(card.vatCents)} USt, ${euro(card.grossCents)} brutto`
                        : ''}
                    </p>
                  ) : null}
                  {card.allocatedAmps != null ? (
                    <p className="text-sm">{card.allocatedAmps} A</p>
                  ) : null}
                  {card.profileLabel ? <p className="text-sm">{card.profileLabel}</p> : null}
                  {(card.ocmfTexts ?? []).length > 0 ? (
                    <ul className="space-y-1">
                      {(card.ocmfTexts ?? []).map((text, index) => (
                        <li key={text} className="font-mono text-xs break-all">
                          {text}
                          {(card.ocmfVerified ?? [])[index] ? ' bestanden' : ' nicht bestanden'}
                        </li>
                      ))}
                    </ul>
                  ) : card.sessionId ? (
                    <p className="text-sm">Kein Datensatz angekommen.</p>
                  ) : null}
                  {card.sessionId ? (
                    <OcmfPasteForm sessionId={card.sessionId} cardId={card.id} />
                  ) : null}
                  {notice[`connector:${card.id}`] ? (
                    <p className="text-sm">{notice[`connector:${card.id}`]}</p>
                  ) : null}
                  {card.interface === 'ocpp' ? (
                    <ConnectorLimitForm
                      key={card.limitAmps ?? 'none'}
                      cardId={card.id}
                      limitAmps={card.limitAmps}
                    />
                  ) : null}
                  {card.canCommand ? (
                    <ConnectorCommands
                      card={card}
                      vehicles={overview.vehicles}
                      pending={pending === `connector:${card.id}`}
                      onStart={(vehicleId, idTag) =>
                        void run(
                          `connector:${card.id}`,
                          `/fleet/energy/connectors/${card.id}/start`,
                          {
                            vehicleId,
                            idTag,
                          },
                        )
                      }
                      onStop={() =>
                        ask(
                          `connector:${card.id}`,
                          'Ladung an diesem Anschluss beenden?',
                          `/fleet/energy/connectors/${card.id}/stop`,
                        )
                      }
                      onUnlock={() =>
                        ask(
                          `connector:${card.id}`,
                          'Diesen Anschluss entriegeln?',
                          `/fleet/energy/connectors/${card.id}/unlock`,
                        )
                      }
                    />
                  ) : null}
                  {card.sessionId ? (
                    <DepartureForm
                      key={card.departureAt ?? 'none'}
                      sessionId={card.sessionId}
                      cardId={card.id}
                      departureAt={card.departureAt}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

export function FleetEnergyPage() {
  usePageTitle('E-Management');
  const energy = useFleetEnergy();
  const save = useSaveFleetEnergyGroup();

  return (
    <div className="space-y-6">
      <PlatformPageHeader
        title="E-Management"
        description="Ladegruppen und die verteilten Ampere."
      />
      {energy.isPending ? <p className="text-sm text-muted-foreground">Wird geladen …</p> : null}
      {energy.isError ? (
        <p className="text-sm text-destructive">E-Management nicht erreichbar</p>
      ) : null}
      {energy.data ? <EnergyBody overview={energy.data} save={save} /> : null}
    </div>
  );
}
